import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Language as LanguageIcon,
  Celebration as CelebrationIcon,
  AccountBalance as AccountBalanceIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
  AutoAwesome as AutoAwesomeIcon,
  Close as CloseIcon,
  Bookmark as BookmarkIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import ProModal from '../../common/ProModal';
import { useTheme } from '@mui/material/styles';

const CulturesEditor = ({ 
  cultures = [], 
  onAddCulture, 
  onEditCulture, 
  onDeleteCulture 
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const accentColor = isDarkMode ? '#e0c3ff' : '#4a148c';
  const accentBg = isDarkMode ? 'rgba(126, 87, 194, 0.22)' : 'rgba(126, 87, 194, 0.06)';
  const accentBorder = isDarkMode ? 'rgba(224, 195, 255, 0.45)' : 'rgba(126, 87, 194, 0.15)';
  const chipBg = isDarkMode ? 'rgba(224, 195, 255, 0.15)' : 'rgba(142, 36, 170, 0.12)';
  const chipBgHover = isDarkMode ? 'rgba(224, 195, 255, 0.25)' : 'rgba(142, 36, 170, 0.2)';
  const templateBtnBorder = isDarkMode ? 'rgba(224, 195, 255, 0.5)' : 'rgba(142, 36, 170, 0.35)';

  const cardBg = isDarkMode ? 'rgba(20, 15, 30, 0.85)' : '#fff';
  const textPrimary = isDarkMode ? '#f3e8ff' : '#4a148c';
  const textSecondary = isDarkMode ? 'rgba(243,232,255,0.75)' : 'text.secondary';

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCulture, setSelectedCulture] = useState(null);
  const [newCulture, setNewCulture] = useState({
    name: '',
    description: '',
    details: '',
    tags: [],
    importance: 'medium',
    traditions: '',
    values: '',
    socialStructure: '',
    languages: '',
    type: ''
  });

  // New state for custom templates
  const [customTemplates, setCustomTemplates] = useState([]);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [newTemplateIcon, setNewTemplateIcon] = useState('tribal');

  const importanceLevels = [
    { value: 'low', label: 'Low', color: 'default' },
    { value: 'medium', label: 'Medium', color: 'primary' },
    { value: 'high', label: 'High', color: 'secondary' },
    { value: 'critical', label: 'Critical', color: 'error' }
  ];

  // Culture types for manual selection
  const cultureTypes = [
    { value: 'tribal', label: 'Tribal', icon: <PeopleIcon fontSize="small" /> },
    { value: 'feudal', label: 'Feudal', icon: <AccountBalanceIcon fontSize="small" /> },
    { value: 'imperial', label: 'Imperial', icon: <AccountBalanceIcon fontSize="small" /> },
    { value: 'mercantile', label: 'Mercantile', icon: <LanguageIcon fontSize="small" /> },
    { value: 'nomadic', label: 'Nomadic', icon: <PeopleIcon fontSize="small" /> },
    { value: 'theocratic', label: 'Theocratic', icon: <CelebrationIcon fontSize="small" /> },
    { value: 'utopian', label: 'Utopian', icon: <CelebrationIcon fontSize="small" /> },
    { value: 'democratic', label: 'Democratic', icon: <PeopleIcon fontSize="small" /> },
    { value: 'technocratic', label: 'Technocratic', icon: <LanguageIcon fontSize="small" /> },
    { value: 'custom', label: 'Custom Type', icon: <InfoOutlinedIcon fontSize="small" /> },
  ];

  // Culture templates - optimized for film/screenwriting with cinematic details
  const cultureTemplates = [
    { 
      name: 'tribal', 
      label: 'Tribal Society', 
      icon: <PeopleIcon fontSize="small" />,
      description: 'Primal bonds of blood and tradition in a world before nations',
      data: {
        importance: 'medium',
        description: 'A people bound by blood, ritual, and the land itself. Ancient ways persist because they work—and because the ancestors demand it.',
        traditions: 'Rites of passage that leave visible marks. Seasonal ceremonies around fire and feast. Stories told only at certain times, in certain places. Taboos that outsiders unknowingly violate.',
        values: 'The tribe survives or no one does. Elders speak with the voice of ancestors. Debts of blood must be paid. Hospitality is sacred—so is vengeance.',
        socialStructure: 'Elders council for wisdom, war chief for conflict, shaman for the spirit world. Everyone has a role; those without one are dangerous. Outsiders must prove themselves or remain suspect.',
        languages: 'Words carry power—names are earned, not given. Certain things cannot be spoken aloud. Gestures and silence communicate as much as speech.',
        details: '**Visual Identity:** Earth tones, animal motifs, ritual scarification or tattoos, handcrafted everything.\n\n**Cinematic Moments:** Firelit ceremonies, the hunt, trials of worthiness, the outsider earning trust.\n\n**Conflict Hooks:** A broken taboo threatens the tribe. The old ways clash with a changing world. A prophecy names an unlikely hero.',
        mood: 'Ancient and primal. Survival is earned daily. The spirits are always watching.',
        visualStyle: 'Firelight and shadow. Faces painted for war or ceremony. Vast wilderness dwarfing human figures.',
        characterTypes: 'The wise elder, the young warrior proving themselves, the outcast, the shaman who sees too much.'
      }
    },
    { 
      name: 'feudal', 
      label: 'Feudal Society', 
      icon: <AccountBalanceIcon fontSize="small" />,
      description: 'Lords and peasants bound by oath, blood, and the weight of tradition',
      data: {
        importance: 'high',
        description: 'A world of castles and cottages where your birth determines your fate—unless you seize another. Honor is everything, and everyone is watching.',
        traditions: 'Oaths sworn on sacred relics. Tournaments where reputations are made and broken. Feast days that empty the larder. The lord\'s justice dispensed in the great hall.',
        values: 'Loyalty unto death. A knight\'s word is unbreakable—supposedly. Blood tells. The peasants exist to serve; the nobles exist to protect. Everyone knows their place.',
        socialStructure: 'The lord in his castle, knights who owe him service, clergy who answer to God and Rome, peasants bound to the land. Marriage is alliance. Bastards are dangerous.',
        languages: 'The nobility speaks differently than commoners—literally. Latin for the church, French for the court, the local tongue for everyone else. Heraldry is a language of its own.',
        details: '**Visual Identity:** Heraldic colors and symbols, armor and fine fabrics, stark contrast between castle luxury and village poverty.\n\n**Cinematic Moments:** The tournament, the siege, the secret meeting, the peasant uprising, forbidden love across class lines.\n\n**Conflict Hooks:** Disputed succession. A lord who breaks faith. A commoner who rises too high. Invaders at the border.',
        mood: 'Rigid hierarchy masking constant scheming. Beauty and brutality side by side.',
        visualStyle: 'Stone and tapestry. Candlelit halls. Mud and blood in the village. Banners snapping in the wind.',
        characterTypes: 'The honorable knight, the scheming lord, the ambitious younger son, the peasant with a secret, the lady who plays the game.'
      }
    },
    { 
      name: 'imperial', 
      label: 'Imperial Culture', 
      icon: <AccountBalanceIcon fontSize="small" />,
      description: 'The weight of civilization—glorious, corrupt, and crumbling at the edges',
      data: {
        importance: 'critical',
        description: 'An empire that has stood for centuries, so vast its rulers have never seen its borders. Civilization at its height—and perhaps its end.',
        traditions: 'Triumphs for victorious generals. Games that distract the masses. Bureaucratic rituals older than anyone remembers. The cult of the divine ruler.',
        values: 'Civilization versus barbarism. Law and order above all. Duty to the state. The glory of the empire reflects on all its citizens—even as it demands everything from them.',
        socialStructure: 'Citizens and subjects, patricians and plebeians, slaves and freedmen. The bureaucracy is a world unto itself. Military service offers advancement. Corruption is endemic.',
        languages: 'The imperial tongue is the language of law, commerce, and culture across a hundred conquered peoples. Local languages persist in homes and hearts.',
        details: '**Visual Identity:** Monumental architecture, togas and uniforms, eagles and laurels, the contrast between marble forums and crowded slums.\n\n**Cinematic Moments:** The triumph, the senate debate, the gladiatorial arena, the frontier outpost, the palace coup.\n\n**Conflict Hooks:** The succession crisis. Barbarians at the gates. A general with too much ambition. The provinces in revolt.',
        mood: 'Grand and decadent. The machinery of civilization grinding on. Greatness and rot intertwined.',
        visualStyle: 'Columns and statues. Crowds and spectacle. The lone figure against imperial grandeur. Torchlit conspiracies.',
        characterTypes: 'The idealistic senator, the cynical general, the scheming courtier, the slave who knows too much, the emperor losing grip.'
      }
    },
    { 
      name: 'mercantile', 
      label: 'Mercantile Society', 
      icon: <LanguageIcon fontSize="small" />,
      description: 'Where gold speaks louder than blood and everything has a price',
      data: {
        importance: 'high',
        description: 'A society where wealth is power and commerce is king. Old nobility sneers at new money—while borrowing from it.',
        traditions: 'The handshake that seals a deal. Guild initiations and trade secrets. Festivals celebrating successful ventures. The public auction where fortunes change hands.',
        values: 'Profit is proof of virtue. A contract is sacred. Information is currency. Risk and reward. Your word is your bond—break it and you\'re finished.',
        socialStructure: 'Wealth determines status. Guilds control trades. Banking families rival nobility. Everyone is calculating angles. Marriages are mergers.',
        languages: 'Multilingual by necessity. Numbers are the universal tongue. Jargon for every trade. Coded language for deals that can\'t be spoken openly.',
        details: '**Visual Identity:** Rich fabrics and obvious wealth. Counting houses and warehouses. Ships and caravans. Ledgers and contracts.\n\n**Cinematic Moments:** The negotiation, the auction, the ship coming in (or not), the rival\'s ruin, the deal with the devil.\n\n**Conflict Hooks:** A debt that can\'t be paid. A monopoly to be broken. Smuggling and piracy. The merchant who sold something they shouldn\'t have.',
        mood: 'Everything is transactional. Smiles hide calculations. Opportunity and betrayal.',
        visualStyle: 'Opulent interiors, busy markets, ships in harbor, the contrast between the counting house and the street.',
        characterTypes: 'The self-made merchant, the heir squandering fortune, the guild master with secrets, the debtor with nothing to lose.'
      }
    },
    { 
      name: 'nomadic', 
      label: 'Nomadic Culture', 
      icon: <PeopleIcon fontSize="small" />,
      description: 'Born in the saddle, free as the wind, feared by those who build walls',
      data: {
        importance: 'medium',
        description: 'A people who carry their world with them. The horizon is home. Settled folk are soft—and their cities are ripe for the taking.',
        traditions: 'The great gathering when scattered bands reunite. Contests of horsemanship and archery. Marriages that forge alliances between clans. The raid as rite of passage.',
        values: 'Freedom above all. Strength earns respect. Hospitality to guests, no mercy to enemies. The herd is wealth. A warrior\'s reputation is immortal.',
        socialStructure: 'Clans led by khans chosen for ability. Women often hold power over the household and herds. Shamans bridge the spirit world. Slaves taken in raids.',
        languages: 'Rich vocabulary for horses, weather, and terrain. Poetry and song preserve history. Oaths are binding—witnessed by sky and earth.',
        details: '**Visual Identity:** Leather and fur, horses everywhere, portable dwellings, weapons always at hand, jewelry as portable wealth.\n\n**Cinematic Moments:** The thundering charge, the vast empty steppe, the gathering of clans, the outsider learning their ways.\n\n**Conflict Hooks:** A new khan unites the tribes. The settled lands grow too tempting. A blood feud between clans. The old ways versus new opportunities.',
        mood: 'Wild and free. The thrill of the hunt and raid. Loyalty fierce as steppe wind.',
        visualStyle: 'Endless horizons. Riders silhouetted against sky. Felt tents glowing at night. Dust and thunder of hooves.',
        characterTypes: 'The ambitious young khan, the wise woman, the legendary archer, the captive who becomes one of them.'
      }
    },
    { 
      name: 'theocratic', 
      label: 'Theocratic Society', 
      icon: <CelebrationIcon fontSize="small" />,
      description: 'Where faith is law and doubt is death—but true believers see paradise',
      data: {
        importance: 'high',
        description: 'God\'s kingdom on earth, or the closest mortals can build. The priests speak for the divine—question them at your peril.',
        traditions: 'Daily prayers structuring time itself. Pilgrimages that prove devotion. Holy days when normal rules suspend. Confession, penance, and absolution.',
        values: 'Faith above reason. Obedience to divine law. Purity of body and soul. The afterlife matters more than this one. Heresy is the ultimate crime.',
        socialStructure: 'Priests at the apex, interpreting divine will. Religious orders with specific functions. The faithful ranked by devotion and orthodoxy. Heretics and unbelievers are outside protection.',
        languages: 'Sacred texts in ancient tongues. Prayers and formulas with precise wording. Theological vocabulary distinguishing fine points of doctrine.',
        details: '**Visual Identity:** Religious symbols everywhere. Ceremonial robes and humble habits. Temples dominating the skyline. The contrast between spiritual beauty and inquisitorial terror.\n\n**Cinematic Moments:** The grand ceremony, the private crisis of faith, the inquisition, the miracle (real or staged), the heretic\'s choice.\n\n**Conflict Hooks:** A schism in the faith. A prophet with a new message. The inquisitor who doubts. Sacred relics with dangerous power.',
        mood: 'Awe and terror. Absolute certainty masking desperate doubt. The weight of eternity.',
        visualStyle: 'Soaring architecture. Incense and candlelight. Crowds of the faithful. The lone figure before the altar.',
        characterTypes: 'The true believer, the corrupt priest, the secret doubter, the fanatic, the saint who may be mad.'
      }
    },
    { 
      name: 'warrior', 
      label: 'Warrior Culture', 
      icon: <PeopleIcon fontSize="small" />,
      description: 'Glory in battle, honor in death—a society forged in blood',
      data: {
        importance: 'high',
        description: 'A people who measure worth in combat. Peace is preparation for war. The greatest shame is to die in bed.',
        traditions: 'Ritual combat to settle disputes. War dances before battle. Trophy-taking from fallen enemies. Scars displayed with pride. Songs of legendary warriors.',
        values: 'Courage above all. Death before dishonor. Loyalty to war-brothers. Skill at arms is the highest virtue. The weak serve or perish.',
        socialStructure: 'Warriors at the top, everyone else exists to support them. Rank earned through combat. Veterans command respect. Women may fight or may be prizes—culture varies.',
        languages: 'Rich vocabulary for weapons, tactics, and types of courage. Insults are an art form. Boasting is expected. Silence before battle.',
        details: '**Visual Identity:** Weapons as status symbols. Scars and war paint. Trophies displayed. Functional clothing that doesn\'t impede movement.\n\n**Cinematic Moments:** The duel, the battle charge, the warrior\'s last stand, the young fighter earning their name, the veteran facing one last fight.\n\n**Conflict Hooks:** A war that cannot be won. An enemy that fights differently. The warrior who questions the code. Peace threatening their identity.',
        mood: 'Adrenaline and brotherhood. The simplicity of combat. Honor as cage and liberation.',
        visualStyle: 'Steel and blood. Training grounds. The calm before battle. Bodies on the field after.',
        characterTypes: 'The legendary champion, the untested youth, the scarred veteran, the warrior-poet, the one who fights for something beyond glory.'
      }
    },
    { 
      name: 'scholarly', 
      label: 'Scholarly Society', 
      icon: <LanguageIcon fontSize="small" />,
      description: 'Knowledge is power—and they\'ve been accumulating both for centuries',
      data: {
        importance: 'medium',
        description: 'A culture that venerates learning above all. Their libraries are fortresses, their scholars are weapons, and they remember everything.',
        traditions: 'Debates that can last for days. Graduation ceremonies older than nations. The copying of texts as sacred duty. Academic rivalries spanning generations.',
        values: 'Truth through study. Preserve knowledge at any cost. Ignorance is the enemy. Patience and precision. The unexamined life is not worth living.',
        socialStructure: 'Hierarchy based on knowledge and academic achievement. Masters and apprentices. Competing schools of thought. Those who know versus those who merely believe.',
        languages: 'Precise terminology for everything. Dead languages kept alive. Notation systems incomprehensible to outsiders. The right word matters.',
        details: '**Visual Identity:** Robes and scrolls. Libraries and laboratories. Ink-stained fingers. The weight of accumulated knowledge in architecture.\n\n**Cinematic Moments:** The crucial discovery, the academic rivalry turning deadly, forbidden knowledge tempting, the scholar in the field proving theory.\n\n**Conflict Hooks:** Knowledge that must be suppressed. A discovery that changes everything. The scholar who knows too much. Ancient texts with dangerous instructions.',
        mood: 'Quiet intensity. The thrill of discovery. Knowledge as burden and weapon.',
        visualStyle: 'Candlelit studies. Towering bookshelves. The single brilliant mind. Dust motes in light through high windows.',
        characterTypes: 'The obsessed researcher, the ambitious student, the keeper of secrets, the scholar-turned-adventurer, the one who published too soon.'
      }
    },
    { 
      name: 'seafaring', 
      label: 'Seafaring Culture', 
      icon: <LanguageIcon fontSize="small" />,
      description: 'Salt in their blood, the horizon in their eyes—born to the waves',
      data: {
        importance: 'medium',
        description: 'A people who belong to the sea more than any land. Every family has lost someone to the deep—and sent someone else to follow.',
        traditions: 'Launching ceremonies and ship burials. Tattoos marking voyages and rank. Superstitions taken deadly seriously. The captain\'s word is absolute law at sea.',
        values: 'The sea provides and the sea takes. Skill matters more than birth. A ship\'s crew is family. Never make promises the sea won\'t let you keep.',
        socialStructure: 'Captains and crews. Shipwright families with secrets. Harbor masters with power. Those who sail and those who wait. The drowned are never truly gone.',
        languages: 'Nautical terminology incomprehensible to landlubbers. Weather-reading as language. Signal flags and horn calls. Songs that keep rhythm for work.',
        details: '**Visual Identity:** Weathered faces and calloused hands. Ships as works of art. Harbor towns clinging to the coast. The contrast between sea and shore.\n\n**Cinematic Moments:** The storm at sea, the distant shore sighted, the ship battle, the sailor returning home changed, the one who never came back.\n\n**Conflict Hooks:** The voyage to unknown waters. Pirates or privateers. The thing pulled up in the nets. A captain\'s obsession.',
        mood: 'Freedom and danger. The call of distant shores. Salt spray and creaking timber.',
        visualStyle: 'Endless water and sky. Ships as isolated worlds. The harbor at dawn. Storms that dwarf human effort.',
        characterTypes: 'The legendary captain, the first-voyage youth, the sailor with a secret, the one who waits on shore, the sea-witch.'
      }
    },
    { 
      name: 'underground', 
      label: 'Underground Society', 
      icon: <PeopleIcon fontSize="small" />,
      description: 'Dwellers in darkness who\'ve built a world the surface never sees',
      data: {
        importance: 'medium',
        description: 'A civilization beneath the earth—whether by choice, necessity, or ancient exile. They\'ve adapted to darkness and learned its secrets.',
        traditions: 'Festivals of artificial light. Coming-of-age journeys through dangerous tunnels. Ancestor crypts as sacred spaces. The mapping of new passages.',
        values: 'The community is the only warmth. Waste nothing. Sound carries—discretion is survival. The surface is dangerous, foreign, or forbidden.',
        socialStructure: 'Those who know the ways and those who follow. Guilds controlling essential resources. Strict population controls. Exile to the surface as ultimate punishment.',
        languages: 'Whisper-speech for stealth. Echo-location clicks. Terminology for darkness, stone, and depth that surface languages lack.',
        details: '**Visual Identity:** Pale skin, large eyes. Bioluminescence and careful flame. Carved stone cities. Fungus gardens and underground lakes.\n\n**Cinematic Moments:** The first sight of the sun, the cave-in, the discovery of a new cavern, the surface-dweller learning to see in darkness.\n\n**Conflict Hooks:** The tunnels are collapsing. Something worse lives deeper. The surface world is encroaching. A path to the surface—opportunity or threat?',
        mood: 'Claustrophobic intimacy. Secrets in the dark. Adaptation as identity.',
        visualStyle: 'Pools of light in vast darkness. Crystalline formations. The weight of stone above. Eyes reflecting torchlight.',
        characterTypes: 'The guide who knows every passage, the exile from above, the one who dreams of sky, the keeper of the deep places.'
      }
    },
  ];

  const handleOpenDialog = (culture = null) => {
    if (culture) {
      setSelectedCulture(culture);
      setNewCulture({
        ...culture,
        tags: culture.tags || []
      });
    } else {
      setSelectedCulture(null);
      setNewCulture({
        name: '',
        description: '',
        details: '',
        tags: [],
        importance: 'medium',
        traditions: '',
        values: '',
        socialStructure: '',
        languages: '',
        type: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSaveCulture = () => {
    const cultureToSave = {
      ...newCulture,
      id: selectedCulture ? selectedCulture.id : Date.now().toString()
    };

    if (selectedCulture) {
      onEditCulture(cultureToSave);
    } else {
      onAddCulture(cultureToSave);
    }

    handleCloseDialog();
  };

  // Function to apply culture template
  const applyCultureTemplate = (templateName) => {
    const template = cultureTemplates.find(t => t.name === templateName);
    if (template) {
      // Build cinematic details from new fields
      const cinematicDetails = template.data.details + 
        (template.data.mood ? `\n\n**Mood:** ${template.data.mood}` : '') +
        (template.data.visualStyle ? `\n\n**Visual Style:** ${template.data.visualStyle}` : '') +
        (template.data.characterTypes ? `\n\n**Character Archetypes:** ${template.data.characterTypes}` : '');
      
      setNewCulture({
        ...newCulture,
        description: template.data.description,
        traditions: template.data.traditions,
        values: template.data.values,
        socialStructure: template.data.socialStructure,
        languages: template.data.languages,
        details: cinematicDetails,
        importance: template.data.importance,
        type: templateName
      });
    }
  };

  // Function to save the current culture as a template
  const saveAsTemplate = () => {
    if (!newTemplateName.trim()) {
      return;
    }
    
    // Create a new template from the current culture data
    const newTemplate = {
      name: `custom-${Date.now()}`,
      label: newTemplateName,
      icon: cultureTemplates.find(t => t.name === newTemplateIcon)?.icon || <PeopleIcon fontSize="small" />,
      description: newTemplateDescription || 'Custom culture template',
      data: {
        importance: newCulture.importance || 'medium',
        description: newCulture.description || '',
        traditions: newCulture.traditions || '',
        values: newCulture.values || '',
        socialStructure: newCulture.socialStructure || '',
        languages: newCulture.languages || '',
        details: newCulture.details || ''
      },
      isCustom: true
    };
    
    setCustomTemplates([...customTemplates, newTemplate]);
    setShowSaveTemplateDialog(false);
    setNewTemplateName('');
    setNewTemplateDescription('');
    setNewTemplateIcon('tribal');
  };
  
  // Function to apply a custom template
  const applyCustomTemplate = (templateName) => {
    const template = customTemplates.find(t => t.name === templateName);
    if (template) {
      setNewCulture({
        ...newCulture,
        description: template.data.description,
        traditions: template.data.traditions,
        values: template.data.values,
        socialStructure: template.data.socialStructure,
        languages: template.data.languages,
        details: template.data.details,
        importance: template.data.importance
      });
    }
  };
  
  // Function to delete a custom template
  const deleteCustomTemplate = (templateName) => {
    setCustomTemplates(customTemplates.filter(t => t.name !== templateName));
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Tooltip title="Add new culture" arrow>
          <span>
            <IconButton
              onClick={() => handleOpenDialog()}
              aria-label="Add new culture"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                color: accentColor,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: accentBg
                }
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Typography variant="h5" component="h2" sx={{ 
          fontWeight: 600,
          color: accentColor
        }}>
          Cultures
        </Typography>
      </Box>

      {cultures.length === 0 ? (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            textAlign: 'center', 
            borderRadius: 2,
            border: `1px dashed ${accentBorder}`,
            bgcolor: isDarkMode
              ? 'rgba(74, 20, 140, 0.25)'
              : 'rgba(237, 231, 246, 0.6)'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: textPrimary }}>
            No cultures added yet
          </Typography>
          <Typography variant="body2" color={textSecondary}>
            Tap the “+” icon to add your first culture and start defining societies.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {cultures.map(culture => (
            <Grid item xs={12} sm={6} md={4} key={culture.id}>
              <Card sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                borderRadius: 1,
                overflow: 'hidden',
                boxShadow: isDarkMode ? '0 12px 30px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.08)',
                transition: 'transform 0.3s, box-shadow 0.3s',
                border: `1px solid ${isDarkMode ? 'rgba(224,195,255,0.25)' : 'rgba(0,0,0,0.05)'}`,
                backgroundColor: cardBg,
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: isDarkMode ? '0 18px 35px rgba(0,0,0,0.65)' : '0 12px 20px rgba(0,0,0,0.12)'
                }
              }}>
                <Box sx={{ 
                  p: 1, 
                  background: isDarkMode ? 'linear-gradient(135deg, rgba(149,117,205,0.35), rgba(103,58,183,0.15))' : 'linear-gradient(135deg, #9575cd40, #9575cd10)',
                  borderBottom: `1px solid ${isDarkMode ? 'rgba(224,195,255,0.25)' : 'rgba(0,0,0,0.05)'}`
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600,
                      color: textPrimary
                    }}>
                      {culture.name}
                    </Typography>
                    {culture.importance && (
                      <Chip 
                        size="small" 
                        label={culture.importance.charAt(0).toUpperCase() + culture.importance.slice(1)} 
                        color={
                          culture.importance === 'high' ? 'error' : 
                          culture.importance === 'medium' ? 'primary' : 
                          'default'
                        }
                        sx={{ 
                          fontWeight: 500,
                          borderRadius: '4px',
                          height: 22,
                          fontSize: '0.7rem'
                        }}
                      />
                    )}
                  </Box>
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Typography color="textSecondary" variant="body2" sx={{ 
                    mb: 2,
                    fontStyle: 'italic',
                    color: textSecondary
                  }}>
                    {culture.description}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ 
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    color: 'text.primary',
                    mb: 2
                  }}>
                    {culture.details}
                  </Typography>
                  {culture.tags && culture.tags.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {culture.tags.map((tag, idx) => (
                        <Chip
                          key={idx}
                          label={tag}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            borderRadius: '4px',
                            backgroundColor: chipBg,
                            borderColor: isDarkMode ? 'rgba(224,195,255,0.4)' : 'rgba(0,0,0,0.12)',
                            '& .MuiChip-label': {
                              px: 1,
                              fontSize: '0.7rem'
                            }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
                <CardActions sx={{ 
                  p: 1.5, 
                  pt: 0.5,
                  borderTop: '1px solid rgba(0,0,0,0.05)',
                  justifyContent: 'flex-end',
                  backgroundColor: 'rgba(0,0,0,0.01)'
                }}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(culture)}
                    sx={{ 
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      mr: 1,
                      color: 'primary.main'
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => onDeleteCulture(culture.id)}
                    sx={{ 
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      color: 'error.main'
                    }}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <ProModal
        open={openDialog}
        onClose={handleCloseDialog}
        title={selectedCulture ? 'Edit Culture' : 'Add New Culture'}
        eyebrow="WORLD BUILDING"
        icon={<PeopleIcon />}
        maxWidth="lg"
        actions={(
          <>
            <Button 
              onClick={handleCloseDialog} 
              color="inherit"
              variant="outlined"
              startIcon={<CloseIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                mr: 1,
                '&:hover': {
                  bgcolor: 'rgba(106, 27, 154, 0.06)',
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSaveCulture} 
              variant="contained" 
              startIcon={<SaveIcon />}
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(120deg, #ab47bc, #7e57c2)',
                '&:hover': {
                  background: 'linear-gradient(120deg, #9c27b0, #673ab7)'
                }
              }}
            >
              {selectedCulture ? 'Update Culture' : 'Add Culture'}
            </Button>
          </>
        )}
      >
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {/* Basic Information Section */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: accentBg, borderRadius: 1, border: `1px solid ${accentBorder}` }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <InfoOutlinedIcon sx={{ mr: 1, fontSize: '1rem' }} /> Basic Information
                </Typography>
              
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Culture Name"
                      fullWidth
                      value={newCulture.name}
                      onChange={(e) => setNewCulture({ ...newCulture, name: e.target.value })}
                      required
                      placeholder="Give your culture a memorable name"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PeopleIcon sx={{ color: accentColor }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                      <InputLabel>Narrative Importance</InputLabel>
                      <Select
                        value={newCulture.importance}
                        label="Narrative Importance"
                        onChange={(e) => setNewCulture({ ...newCulture, importance: e.target.value })}
                      >
                        {importanceLevels.map((level) => (
                          <MenuItem key={level.value} value={level.value}>
                            {level.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        How significant is this culture to your story?
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Brief Description"
                      fullWidth
                      multiline
                      rows={2}
                      value={newCulture.description}
                      onChange={(e) => setNewCulture({ ...newCulture, description: e.target.value })}
                      placeholder="A logline for this culture—capture its essence as you would pitch it to a director"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                    />
                  </Grid>
                </Grid>
              </Paper>
              
              {/* Culture Classification */}
              <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: 'rgba(126, 87, 194, 0.06)', borderRadius: 1, border: '1px solid rgba(126, 87, 194, 0.15)' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#6a1b9a', display: 'flex', alignItems: 'center' }}>
                  <PeopleIcon sx={{ mr: 1, fontSize: '1rem' }} /> Culture Classification
                </Typography>
              
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                      <InputLabel>Culture Type</InputLabel>
                      <Select
                        value={newCulture.type}
                        label="Culture Type"
                        onChange={(e) => setNewCulture({ ...newCulture, type: e.target.value })}
                      >
                        {cultureTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        What category of culture is this?
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
              
              {/* Cultural Details */}
              <Paper elevation={0} sx={{ p: 2, bgcolor: accentBg, borderRadius: 1, border: `1px solid ${accentBorder}` }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                  <CelebrationIcon sx={{ mr: 1, fontSize: '1rem' }} /> Cultural Details
                </Typography>
              
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Traditions"
                      fullWidth
                      value={newCulture.traditions || ''}
                      onChange={(e) => setNewCulture({ ...newCulture, traditions: e.target.value })}
                      placeholder="Rituals, ceremonies, and customs that could be filmed. What visual traditions define this culture?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <CelebrationIcon sx={{ color: accentColor }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Values"
                      fullWidth
                      value={newCulture.values || ''}
                      onChange={(e) => setNewCulture({ ...newCulture, values: e.target.value })}
                      placeholder="What do they fight for? Die for? What beliefs drive character motivation and conflict?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <AccountBalanceIcon sx={{ color: accentColor }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Social Structure"
                      fullWidth
                      value={newCulture.socialStructure || ''}
                      onChange={(e) => setNewCulture({ ...newCulture, socialStructure: e.target.value })}
                      placeholder="Power dynamics and hierarchies. Who's on top? Who's oppressed? Where's the tension?"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PeopleIcon sx={{ color: '#e65100' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Languages"
                      fullWidth
                      value={newCulture.languages || ''}
                      onChange={(e) => setNewCulture({ ...newCulture, languages: e.target.value })}
                      placeholder="How do they communicate? Accents, dialects, forbidden words, gestures that speak volumes."
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, mb: 2 }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LanguageIcon sx={{ color: accentColor }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            
            {/* Templates - Collapsible */}
            <Grid item xs={12} md={4}>
              <Accordion 
                defaultExpanded={false}
                sx={{ 
                  bgcolor: accentBg, 
                  borderRadius: '1 !important', 
                  border: `1px solid ${accentBorder}`,
                  '&:before': { display: 'none' },
                  boxShadow: 'none',
                  mb: 3
                }}
              >
                <AccordionSummary 
                  expandIcon={<ExpandMoreIcon sx={{ color: accentColor }} />}
                  sx={{ 
                    minHeight: 48,
                    '& .MuiAccordionSummary-content': { my: 1 }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: accentColor, display: 'flex', alignItems: 'center' }}>
                    <AutoAwesomeIcon sx={{ mr: 1, fontSize: '1rem' }} /> Quick Templates
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Apply a template to quickly fill in common culture types.
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: '#e65100' }}>
                      Standard Templates
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {cultureTemplates.map((template) => (
                        <Chip
                          key={template.name}
                          icon={<PeopleIcon />}
                          label={template.label}
                          onClick={() => applyCultureTemplate(template.name)}
                          sx={{
                            bgcolor: chipBg,
                            color: accentColor,
                            '&:hover': {
                              bgcolor: chipBgHover,
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                  
                  {customTemplates && customTemplates.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: accentColor }}>
                        Custom Templates
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {customTemplates.map((template) => (
                          <Chip
                            key={template.name}
                            icon={<BookmarkIcon />}
                            label={template.name}
                            onClick={() => applyCustomTemplate(template.name)}
                            sx={{
                              bgcolor: chipBg,
                              color: accentColor,
                              '&:hover': {
                                bgcolor: chipBgHover,
                              }
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>

              <Paper elevation={0} sx={{ p: 2, bgcolor: 'rgba(126, 87, 194, 0.06)', borderRadius: 1, border: '1px solid rgba(126, 87, 194, 0.15)' }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#6a1b9a', display: 'flex', alignItems: 'center' }}>
                  <BookmarkIcon sx={{ mr: 1, fontSize: '1rem' }} /> Save as Template
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Save your current settings as a custom template for future use.
                </Typography>
                
                <TextField
                  label="Template Name"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  fullWidth
                  sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                  placeholder="Enter a name for your template"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BookmarkIcon sx={{ color: accentColor }} />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <Button 
                  variant="outlined" 
                  fullWidth
                  onClick={saveAsTemplate}
                  disabled={!newTemplateName.trim()}
                  startIcon={<SaveIcon />}
                  sx={{ 
                    borderRadius: '8px',
                    borderColor: templateBtnBorder,
                    color: accentColor,
                    '&:hover': {
                      borderColor: '#7b1fa2',
                      bgcolor: chipBgHover,
                    }
                  }}
                >
                  Save Template
                </Button>
              </Paper>
            </Grid>
          </Grid>
      </ProModal>
      
      <ProModal
        open={showSaveTemplateDialog}
        onClose={() => setShowSaveTemplateDialog(false)}
        title="Save as Template"
        eyebrow="WORLD BUILDING"
        icon={<BookmarkIcon />}
        maxWidth="sm"
        actions={(
          <>
            <Button 
              onClick={() => setShowSaveTemplateDialog(false)}
              variant="outlined"
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                mr: 1
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={saveAsTemplate}
              variant="contained"
              sx={{ 
                borderRadius: '8px',
                textTransform: 'none',
                background: 'linear-gradient(120deg, #ab47bc, #7e57c2)',
                '&:hover': {
                  background: 'linear-gradient(120deg, #9c27b0, #673ab7)'
                }
              }}
            >
              Save
            </Button>
          </>
        )}
        contentProps={{ sx: { pt: 3, px: 3 } }}
      >
          <Typography variant="body2" sx={{ mb: 2 }}>
            Save the current culture as a custom template for future use.
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Template Name"
                fullWidth
                value={newTemplateName}
                onChange={(e) => setNewTemplateName(e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Template Description"
                fullWidth
                multiline
                rows={2}
                value={newTemplateDescription}
                onChange={(e) => setNewTemplateDescription(e.target.value)}
                placeholder="Brief description of this template"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Template Icon</InputLabel>
                <Select
                  value={newTemplateIcon}
                  label="Template Icon"
                  onChange={(e) => setNewTemplateIcon(e.target.value)}
                >
                  {cultureTemplates.map(template => (
                    <MenuItem key={template.name} value={template.name}>
                      {template.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
      </ProModal>
    </Box>
  );
};

export default CulturesEditor;
